import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import Logo from "./Logo";
import { API_ENDPOINTS } from "../config/api";

const Footer = () => {
  const currentYear = new Date().getFullYear();



  const [policies, setPolicies] = React.useState([]);

  React.useEffect(() => {
    const fetchPolicies = async () => {
      try {
        const response = await fetch(
          `${API_ENDPOINTS.POLICIES}`
        );
        const data = await response.json();
        if (data.status) {
          setPolicies(data.data);
        }
      } catch (error) {
        console.error("Error fetching policies:", error);
      }
    };

    fetchPolicies();
  }, []);

  // Filter policies by page_type
  const legalPolicies = policies
    .filter((policy) => policy.page_type === "legal")
    .map((policy) => ({
      label: policy.title,
      path: `/legal/${policy.slug}`,
    }));

  const companyPolicies = policies
    .filter((policy) => policy.page_type === "company")
    .map((policy) => ({
      label: policy.title,
      path: `/legal/${policy.slug}`,
    }));

  // Get all API policy titles to check for duplicates
  const allApiPolicyTitles = policies.map((policy) => policy.title.toLowerCase());

  // Static company links
  const staticCompanyLinks = [
    { label: "About Us", path: "/about" },
    { label: "How It Works", path: "/how-it-works" },
    { label: "Careers", path: "/become-worker" },
    { label: "Contact Us", path: "/contact" },
  ];

  // Filter out static links that match API policy titles
  const filteredStaticCompanyLinks = staticCompanyLinks.filter(
    (link) => !allApiPolicyTitles.includes(link.label.toLowerCase())
  );

  const footerLinks = {
    company: [
      ...filteredStaticCompanyLinks, // Static links that don't exist in API
      ...companyPolicies, // Company policies from API
    ],
    services: [
      { label: "House Cleaning", path: "/services" },
      { label: "Plumbing", path: "/services" },
      { label: "Electrical Work", path: "/services" },
      { label: "Cooking Services", path: "/services" },
      { label: "Household Help", path: "/services" },
    ],
    legal: legalPolicies, // Only legal policies from API
  };

  const socialLinks = [
    { icon: Facebook, href: "#", label: "Facebook" },
    { icon: Twitter, href: "#", label: "Twitter" },
    { icon: Instagram, href: "#", label: "Instagram" },
    { icon: Linkedin, href: "#", label: "LinkedIn" },
  ];

  return (
    <footer className="bg-gray-50 border-t border-gray-200">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <Logo className="h-10 w-auto" />
            </Link>
            <p className="text-gray-600 mb-4 max-w-sm">
              Your trusted partner for all home services. Quality, reliability,
              and convenience at your doorstep.
            </p>
            <div className="space-y-2">
              <div className="flex items-center text-gray-600">
                <Phone size={16} className="mr-2" />
                <span>9257652266</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Mail size={16} className="mr-2" />
                <span>support@redqubes.com</span>
              </div>
              <div className="flex items-center text-gray-600">
                <MapPin size={16} className="mr-2" />
                <span>
                  Office No 402 4th Floor Shree G D Tower P No 3 And 99
                  Neelkanth Colony Purani Chungi Ajmer Road Jpr-302021
                </span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Company</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-teal-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Services</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-teal-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-gray-900 font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-gray-600 hover:text-teal-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-600 text-sm mb-4 md:mb-0">
            © {currentYear} Maidly. All rights reserved.
          </p>

          {/* Social Links */}
          <div className="flex space-x-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 hover:bg-teal-600 hover:text-white transition-all"
                  aria-label={social.label}
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
